import { useEffect, useState } from "react";
import { apiFetchBlob } from "@/lib/api";

/**
 * Loads a private, auth-only image (e.g. the firm logo or a signature) and
 * returns an object URL for an `<img src>`. These files live in private media —
 * there is no public media URL (it would expose encrypted client documents
 * stored under the same root) — so they're fetched through an authenticated API
 * endpoint with the bearer token, then exposed as a blob object URL.
 *
 * Returns `undefined` while loading or when there's nothing to load, and revokes
 * the object URL on change/unmount. Pass a cache-busting token in the path (e.g.
 * `?v=<stored-filename>`) so the image reloads after a re-upload even though the
 * endpoint path itself is stable.
 */
export function useAuthedImage(path: string | null | undefined): string | undefined {
  const [src, setSrc] = useState<string>();

  useEffect(() => {
    if (!path) {
      setSrc(undefined);
      return;
    }
    let active = true;
    let objectUrl: string | undefined;
    apiFetchBlob(path)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (active) setSrc(undefined);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  return src;
}
